/*
 * Sidebar — a navigation tree for an application's main menu.
 *
 * Replaces w2ui's w2sidebar, which was the only part of w2ui still in use and
 * cost 1.9MB of library to get one widget. The API below is deliberately close
 * to the one it replaces where those names were already the natural ones
 * (get/add/insert/remove/show/hide/expand/collapse/toggle/select), and
 * deliberately different where w2ui's shape was awkward: handlers receive the
 * payload directly rather than through an `event.detail` wrapper.
 *
 * The thing it does better is filtering. w2ui's show/hide/expand each
 * re-rendered the node they touched, so one keystroke in a menu search redrew
 * hundreds of nodes. Here every node keeps its element, and filtering toggles
 * two classes per node with no rebuilding at all.
 */

import { Component, h, escapeHtml, uid } from './core.js';
import { ICONS, renderIcon, isIconUrl } from './icons.js';
import { EventChannel } from './events.js';

/** Walks a node tree depth first, calling fn(node, parent, depth). */
function walk(nodes, fn, parent = null, depth = 0) {
	for (const node of nodes || []) {
		fn(node, parent, depth);
		if (node.nodes && node.nodes.length) walk(node.nodes, fn, node, depth + 1);
	}
}

export class Sidebar extends Component {
	static get componentName() { return 'sidebar'; }

	static get detachable() { return true; }

	defaults() {
		return {
			/**
			 * The node tree. Each node: { id, text, icon, func, tooltip, group, expanded,
			 * hidden, disabled, class, count, countColor, color, type, nodes }.
			 * `value` is read as `text`, `css` as `class`, and `data` as `nodes`.
			 */
			nodes: [],
			/** Icons-only rail (`collapsed` is accepted as another name for it). */
			flat: false,
			/** Arrow-key navigation and Enter/Space to activate. */
			keyboard: true,
			/** Hover delay in ms before a `mouseEnter` is reported, so passing the pointer over the menu stays quiet. */
			hoverDelay: 500,
			/**
			 * Prefix for each row's DOM id, so a host can find a row with
			 * document.getElementById(prefix + nodeId). Prefer nodeElement(id),
			 * which needs no id scheme and copes with ids that are not valid in
			 * a CSS selector.
			 */
			idPrefix: 'node_',
			/**
			 * Show a node's `tooltip` on hover. The panel is appended to the body so
			 * it escapes the rail's own overflow, which is what makes it usable at
			 * all when the rail is narrow. Set false to handle `mouseEnter` yourself.
			 */
			tooltips: true,
			/** Name, carried for hosts that key other state (tooltips, storage) off it. */
			name: 'sidebar',
		};
	}

	render() {
		const o = this.options;
		if (!this.events) {
			this.events = new EventChannel(this, (name, args) => this._relay(name, args));
			this.data = this._dataApi();
		}
		// Other names for the same options.
		if (o.collapsed !== undefined && o.collapsed !== null) { o.flat = !!o.collapsed; o.collapsed = undefined; }
		if (Array.isArray(o.data)) { o.nodes = o.data; o.data = undefined; }
		if (!Array.isArray(o.nodes)) o.nodes = [];
		this._appliedCss = this._appliedCss || [];
		this._appliedCss.forEach((c) => this.el.classList.remove(c));
		this._appliedCss = String(o.css || '').split(/\s+/).filter(Boolean);
		this._appliedCss.forEach((c) => this.el.classList.add(c));

		this.name = o.name;
		this._index = new Map();
		this._selected = this._selected === undefined ? null : this._selected;
		this.el.classList.toggle('dfc-sidebar--flat', !!o.flat);
		this.el.innerHTML = '';
		this._body = h('div', { className: 'dfc-sidebar__body', role: 'tree' });
		this.el.appendChild(this._body);

		// Index first so parent back-pointers exist before anything is drawn:
		// a host walking up from a node (to expand its ancestors) relies on them.
		// Keys are strings, so a numeric id and the row's data-id find the same node.
		walk(o.nodes, (node, parent) => {
			node.parent = parent;
			if (node.id === undefined || node.id === null || node.id === '') node.id = uid('dfc-sbn');
			if (this._index.has(String(node.id))) {
				console.warn('dfc-sidebar: duplicate node id "' + node.id + '"; the later one is ignored');
				return;
			}
			this._index.set(String(node.id), node);
		});

		this._renderInto(this._body, o.nodes, 0);
		if (this._selected !== null && this._index.has(String(this._selected))) this._paintSelection(this._selected);
		else { this._selected = null; this._syncTabStop(); }
	}

	/** Draws one level and recurses. @param {HTMLElement} host @param {Array} nodes @param {number} depth */
	_renderInto(host, nodes, depth) {
		for (const node of nodes || []) {
			if (this._index.get(String(node.id)) !== node) continue; // a duplicate
			const row = this._renderRow(node, depth);
			host.appendChild(row);
			node._el = row;
			if (node.nodes && node.nodes.length) {
				const kids = h('div', { className: 'dfc-sidebar__children' });
				kids.dataset.parent = String(node.id);
				if (!node.expanded) kids.hidden = true;
				this._renderInto(kids, node.nodes, depth + 1);
				host.appendChild(kids);
				node._kids = kids;
			}
			if (node.hidden) row.hidden = true;
		}
	}

	/** Builds one row. @param {Object} node @param {number} depth @returns {HTMLElement} */
	_renderRow(node, depth) {
		const extra = node.class != null ? node.class : node.css;
		if (node.type === 'separator') {
			const sep = h('div', { className: 'dfc-sidebar__separator' + (extra ? ' ' + extra : ''), role: 'separator' });
			sep.dataset.id = String(node.id);
			return sep;
		}
		const branch = !!(node.nodes && node.nodes.length);
		const row = h('div', {
			className: 'dfc-sidebar__item'
				+ (node.group ? ' dfc-sidebar__item--group' : '')
				+ (branch ? ' dfc-sidebar__item--branch' : '')
				+ (node.disabled ? ' dfc-sidebar__item--disabled' : '')
				+ (extra ? ' ' + extra : ''),
			role: 'treeitem',
			tabIndex: -1,
		});
		row.id = this.options.idPrefix + node.id;
		row.dataset.id = String(node.id);
		// The depth indent is a CSS variable, not an inline padding, so the icons-only rail can drop it.
		if (depth) row.style.setProperty('--dfc-sidebar-depth', String(depth));
		if (branch) row.setAttribute('aria-expanded', node.expanded ? 'true' : 'false');
		if (node.disabled) row.setAttribute('aria-disabled', 'true');
		if (node.color) row.style.setProperty('--dfc-sidebar-item-color', node.color);

		const text = this._text(node);
		const icon = h('span', { className: 'dfc-sidebar__icon' });
		if (node.icon) {
			// A class string keeps its long-standing markup: one span carrying the classes.
			// Built-in names, SVG strings, URLs and { className } go through the icon spec.
			if (typeof node.icon === 'string' && !ICONS[node.icon.trim()] && !/^<svg[\s>]/i.test(node.icon.trim()) && !isIconUrl(node.icon.trim())) icon.appendChild(h('span', { className: node.icon.trim() }));
			else { const i = renderIcon(node.icon); if (i) icon.appendChild(i); }
		}
		row.appendChild(icon);
		row.appendChild(h('span', { className: 'dfc-sidebar__text' }, text));
		const count = Number(node.count);
		if (count > 0) row.appendChild(h('span', { className: 'dfc-sidebar__count dfc-sidebar__count--' + (node.countColor || 'danger'), text: count > 99 ? '99+' : String(count) }));
		// The text is hidden in the rail, so the row carries its name itself.
		if (text) row.setAttribute('aria-label', count > 0 ? text + ' (' + count + ')' : text);
		if (branch) row.appendChild(h('span', { className: 'dfc-sidebar__caret' }));
		return row;
	}

	/** A node's label: `text`, or `value` when there is no text. */
	_text(node) {
		const t = node.text != null ? node.text : node.value;
		return t == null ? '' : String(t);
	}

	bind() {
		this._onClick = (ev) => {
			const row = ev.target.closest ? ev.target.closest('.dfc-sidebar__item') : null;
			if (!row || !this.el.contains(row)) return;
			const node = this._index.get(row.dataset.id);
			if (!node || node.disabled) return;
			// A group header is a label, not a destination.
			if (node.group && !(node.nodes && node.nodes.length)) return;
			this._hideTip();
			// Payload listeners and positional handlers both hear the click; either can veto the default.
			if (!this.events.fire('click', [node.id, ev])) return;
			if (this._destroyed) return;
			if (node.nodes && node.nodes.length) this.toggle(node.id);
			else this.select(node.id);
		};
		this.el.addEventListener('click', this._onClick);

		// Hover is reported once the pointer settles, so sweeping across the menu
		// does not fire a burst of events at whatever is listening.
		this._hoverTimer = null;
		this._hovered = null;
		this._onOver = (ev) => {
			const row = ev.target.closest ? ev.target.closest('.dfc-sidebar__item') : null;
			if (!row || !this.el.contains(row) || row === this._hovered) return;
			this._leaveHovered();
			this._hovered = row;
			const node = this._index.get(row.dataset.id);
			if (!node) return;
			this._hoverTimer = setTimeout(() => {
				this._hoverTimer = null;
				// In the rail the text is hidden, so a node without a tooltip shows its text.
				const tip = node.tooltip || (this.options.flat ? escapeHtml(this._text(node)) : '');
				if (this.options.tooltips && tip) this._showTip(row, tip);
				this.emit('mouseEnter', this._payload({ node, target: row, originalEvent: ev }));
			}, this.options.hoverDelay);
		};
		this._onOut = (ev) => {
			const to = ev.relatedTarget;
			if (to && this.el.contains(to) && to.closest && to.closest('.dfc-sidebar__item') === this._hovered) return;
			this._leaveHovered();
		};
		this.el.addEventListener('mouseover', this._onOver);
		this.el.addEventListener('mouseout', this._onOut);

		if (this.options.keyboard) {
			this._onKey = (ev) => this._key(ev);
			this.el.addEventListener('keydown', this._onKey);
		}
		// A tooltip shown for keyboard focus goes when focus leaves the rail.
		this._onBlur = (ev) => { if (!ev.relatedTarget || !this.el.contains(ev.relatedTarget)) this._hideTip(); };
		this.el.addEventListener('focusout', this._onBlur);
	}

	/** Ends the current hover, cancelling a pending enter and reporting the leave. */
	_leaveHovered() {
		if (this._hoverTimer) { clearTimeout(this._hoverTimer); this._hoverTimer = null; }
		this._hideTip();
		if (!this._hovered) return;
		const node = this._index.get(this._hovered.dataset.id);
		this._hovered = null;
		if (node) this.emit('mouseLeave', this._payload({ node }));
	}

	/**
	 * Shows the hover panel beside a row, preferring its right and flipping to
	 * the left when the viewport has no room.
	 * @param {HTMLElement} row @param {string} html
	 */
	_showTip(row, html) {
		if (!this._tip) {
			this._tip = h('div', { className: 'dfc dfc-sidebar-tip' });
			this._tip.setAttribute('role', 'tooltip');
			document.body.appendChild(this._tip);
		}
		this._tip.innerHTML = html;
		this._tip.style.visibility = 'hidden';
		this._tip.style.display = 'block';
		const at = row.getBoundingClientRect();
		const own = this._tip.getBoundingClientRect();
		const room = window.innerWidth - at.right;
		const left = room >= own.width + 12 ? at.right + 8 : Math.max(4, at.left - own.width - 8);
		let top = at.top + (at.height - own.height) / 2;
		top = Math.max(4, Math.min(top, window.innerHeight - own.height - 4));
		this._tip.style.left = Math.round(left) + 'px';
		this._tip.style.top = Math.round(top) + 'px';
		this._tip.style.visibility = 'visible';
	}

	/** Hides the hover panel if one is showing. */
	_hideTip() { if (this._tip) this._tip.style.display = 'none'; }

	/** Arrow keys move, left/right collapse/expand, Enter and Space activate. */
	_key(ev) {
		const row = ev.target.closest ? ev.target.closest('.dfc-sidebar__item') : null;
		if (!row) return;
		const node = this._index.get(row.dataset.id);
		if (!node) return;
		const visible = this._visibleRows();
		const at = visible.indexOf(row);
		const focus = (el) => {
			if (!el) return;
			Array.prototype.forEach.call(this.el.querySelectorAll('.dfc-sidebar__item'), (r) => { r.tabIndex = r === el ? 0 : -1; });
			el.focus();
			// The rail hides labels, so keyboard users get the tooltip a pointer would.
			if (this.options.flat && this.options.tooltips) {
				const n = this._index.get(el.dataset.id);
				const tip = n && (n.tooltip || escapeHtml(this._text(n)));
				if (tip) this._showTip(el, tip); else this._hideTip();
			}
		};
		switch (ev.key) {
			case 'ArrowDown': ev.preventDefault(); focus(visible[at + 1]); break;
			case 'ArrowUp': ev.preventDefault(); focus(visible[at - 1]); break;
			case 'ArrowRight':
				ev.preventDefault();
				if (node.nodes && node.nodes.length && !node.expanded) this.expand(node.id);
				else focus(visible[at + 1]);
				break;
			case 'ArrowLeft':
				ev.preventDefault();
				if (node.nodes && node.nodes.length && node.expanded) this.collapse(node.id);
				else if (node.parent) focus(node.parent._el);
				break;
			case 'Home': ev.preventDefault(); focus(visible[0]); break;
			case 'End': ev.preventDefault(); focus(visible[visible.length - 1]); break;
			case 'Enter': case ' ':
				ev.preventDefault();
				row.click();
				break;
			default: break;
		}
	}

	/** Rows currently on screen, in document order. @returns {Array<HTMLElement>} */
	_visibleRows() {
		return Array.prototype.filter.call(
			this.el.querySelectorAll('.dfc-sidebar__item'),
			(el) => el.offsetParent !== null
		);
	}

	/** A cancellable event payload. @param {Object} extra @returns {Object} */
	_payload(extra) {
		const payload = Object.assign({ defaultPrevented: false }, extra);
		payload.preventDefault = function () { payload.defaultPrevented = true; };
		return payload;
	}

	/**
	 * Runs before positional handlers on every events.fire: the payload
	 * listeners hear a click too, and their preventDefault vetoes it.
	 */
	_relay(name, args) {
		if (name !== 'click') return;
		const node = this.get(args[0]);
		const payload = this._payload({ node, id: args[0], originalEvent: args[1] || null });
		this.emit('click', payload);
		return !payload.defaultPrevented;
	}

	/**
	 * With a node id and a patch, changes that node in place: text (or value),
	 * icon, count, countColor, color, tooltip, hidden, disabled, class (or css).
	 * With one object, merges options and re-renders, as every component does.
	 * @returns {this}
	 */
	update(idOrOptions, patch) {
		if ((typeof idOrOptions === 'string' || typeof idOrOptions === 'number') && patch && typeof patch === 'object') {
			const node = this.get(idOrOptions);
			if (!node) return this;
			const rest = Object.assign({}, patch);
			delete rest.id; delete rest.parent; delete rest.nodes;
			Object.assign(node, rest);
			if (node._el && node._el.isConnected) {
				let depth = 0; for (let p = node.parent; p; p = p.parent) depth++;
				const hadFocus = node._el === document.activeElement;
				const row = this._renderRow(node, depth);
				if (node.hidden) row.hidden = true;
				node._el.replaceWith(row);
				node._el = row;
				if (node._kids) node._kids.hidden = !!node.hidden || !node.expanded;
				if (this._selected !== null && String(this._selected) === String(node.id)) { row.classList.add('dfc-sidebar__item--selected'); row.setAttribute('aria-selected', 'true'); }
				this._syncTabStop();
				if (hadFocus) row.focus({ preventScroll: true });
			}
			return this;
		}
		return super.update(idOrOptions || {});
	}

	/** The `data` object: the node store as a flat list API (ids of any depth). */
	_dataApi() {
		const self = this;
		const strip = (n) => {
			const out = {};
			Object.keys(n).forEach((k) => { if (k !== 'parent' && k !== '_el' && k !== '_kids') out[k] = k === 'nodes' && Array.isArray(n.nodes) ? n.nodes.map(strip) : n[k]; });
			return out;
		};
		return {
			/** Replaces every node. The selection stays when its id is still there. */
			parse(nodes) { self.setNodes(Array.isArray(nodes) ? nodes : []); return self.data; },
			/** Adds a node at an index among the root nodes (or a `parent` node's children). */
			add(node, index) {
				const list = Array.isArray(node) ? node : [node];
				const parentId = list[0] && list[0].parent != null && typeof list[0].parent !== 'object' ? list[0].parent : null;
				const parent = parentId === null ? null : self.get(parentId);
				const siblings = parent ? (parent.nodes = parent.nodes || []) : self.options.nodes;
				list.forEach((n) => { if (n && typeof n.parent !== 'object') delete n.parent; });
				const at = index === undefined || index === null || index < 0 || index > siblings.length ? siblings.length : index;
				siblings.splice(at, 0, ...list);
				self.render();
				return Array.isArray(node) ? list.map((n) => n.id) : list[0] && list[0].id;
			},
			update(id, patch) { self.update(id, patch); },
			remove(ids) { self.remove(...(Array.isArray(ids) ? ids : [ids])); },
			removeAll() { self.setNodes([]); },
			getItem(id) { return self.get(id) || undefined; },
			exists(id) { return !!self.get(id); },
			getIndex(id) { const n = self.get(id); if (!n) return -1; return (n.parent ? n.parent.nodes : self.options.nodes).indexOf(n); },
			getId(index) { const n = self.options.nodes[index]; return n ? n.id : undefined; },
			getLength() { return self.options.nodes.length; },
			forEach(fn) { self.options.nodes.slice().forEach((n, i) => fn(n, i)); },
			/** A copy of the nodes without the links the sidebar keeps. */
			serialize() { return self.options.nodes.map(strip); },
		};
	}

	/** Same as destroy(). */
	destructor() { this.destroy(); }

	// ── Reading ────────────────────────────────────────────────────────────

	/** The live node for an id, or null. Carries `.parent`. @param {string} id @returns {Object|null} */
	get(id) { return this._index.get(String(id)) || null; }

	/** The row element for an id, with no dependence on the id scheme. @param {string} id @returns {HTMLElement|null} */
	nodeElement(id) { const n = this.get(id); return (n && n._el) || null; }

	/** The selected node's id, or null. @returns {string|null} */
	get selected() { return this._selected; }

	/** Every ancestor id of a node, nearest first. @param {string} id @returns {Array<string>} */
	parentsOf(id) {
		const out = [];
		let n = this.get(id);
		while (n && n.parent) { out.push(n.parent.id); n = n.parent; }
		return out;
	}

	// ── Selection ──────────────────────────────────────────────────────────

	/** Selects a node, revealing it by expanding its ancestors. @param {string} id @returns {this} */
	select(id) {
		const node = this.get(id);
		if (!node) return this;
		this.parentsOf(id).reverse().forEach((p) => this.expand(p));
		this._paintSelection(node.id);
		this._selected = node.id;
		this.emit('select', this._payload({ node }));
		return this;
	}

	/** Clears the selection. @returns {this} */
	unselect() {
		this._paintSelection(null);
		this._selected = null;
		return this;
	}

	/** @param {string|null} id */
	_paintSelection(id) {
		const was = this.el.querySelectorAll('.dfc-sidebar__item--selected');
		Array.prototype.forEach.call(was, (el) => { el.classList.remove('dfc-sidebar__item--selected'); el.removeAttribute('aria-selected'); });
		const el = id == null ? null : this.nodeElement(id);
		if (el) {
			el.classList.add('dfc-sidebar__item--selected');
			el.setAttribute('aria-selected', 'true');
			if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
		}
		this._syncTabStop();
	}

	/** One row is reachable with Tab: the focused one, else the selected one, else the first. */
	_syncTabStop() {
		const rows = this.el.querySelectorAll('.dfc-sidebar__item');
		const active = document.activeElement;
		let stop = null;
		if (active && active.classList && active.classList.contains('dfc-sidebar__item') && this.el.contains(active)) stop = active;
		if (!stop) stop = this.el.querySelector('.dfc-sidebar__item--selected');
		if (!stop) stop = Array.prototype.find.call(rows, (r) => !r.hidden && !r.classList.contains('dfc-sidebar__item--group')) || rows[0] || null;
		Array.prototype.forEach.call(rows, (r) => { r.tabIndex = r === stop ? 0 : -1; });
	}

	// ── Expanding ──────────────────────────────────────────────────────────

	/** @param {string} id @returns {this} */
	expand(id) { return this._setExpanded(id, true); }
	/** @param {string} id @returns {this} */
	collapse(id) { return this._setExpanded(id, false); }
	/** @param {string} id @returns {this} */
	toggle(id) { const n = this.get(id); return n ? this._setExpanded(id, !n.expanded) : this; }

	/** @param {string} id @param {boolean} open @returns {this} */
	_setExpanded(id, open) {
		const node = this.get(id);
		if (!node || !node.nodes || !node.nodes.length) return this;
		if (!!node.expanded === open) return this;
		const payload = this._payload({ node });
		this.emit(open ? 'beforeExpand' : 'beforeCollapse', payload);
		if (payload.defaultPrevented) return this;
		node.expanded = open;
		if (node._kids) node._kids.hidden = !open;
		if (node._el) node._el.setAttribute('aria-expanded', open ? 'true' : 'false');
		this.emit(open ? 'expand' : 'collapse', this._payload({ node }));
		return this;
	}

	/** @returns {this} */
	expandAll() { walk(this.options.nodes, (n) => { if (n.nodes && n.nodes.length) this._setExpanded(n.id, true); }); return this; }
	/** @returns {this} */
	collapseAll() { walk(this.options.nodes, (n) => { if (n.nodes && n.nodes.length) this._setExpanded(n.id, false); }); return this; }

	/** The ids of every open branch, for saving and restoring a view. @returns {Array<string>} */
	expandedIds() {
		const out = [];
		walk(this.options.nodes, (n) => { if (n.expanded && n.nodes && n.nodes.length) out.push(n.id); });
		return out;
	}

	// ── Visibility ─────────────────────────────────────────────────────────

	/** @param {string} id @returns {this} */
	show(id) { return this._setHidden(id, false); }
	/** @param {string} id @returns {this} */
	hide(id) { return this._setHidden(id, true); }

	/** @param {string} id @param {boolean} hidden @returns {this} */
	_setHidden(id, hidden) {
		const node = this.get(id);
		if (!node) return this;
		node.hidden = hidden;
		if (node._el) node._el.hidden = hidden;
		// A branch's children follow the branch, but only while it is open.
		if (node._kids) node._kids.hidden = hidden || !node.expanded;
		return this;
	}

	/**
	 * Filters the tree by text, showing every match and the ancestors needed to
	 * reach it, and opening those ancestors. An empty term restores everything
	 * and puts the expansion back to `restore` if given.
	 *
	 * This is the whole reason the component exists: it touches each node once
	 * and changes classes only, where the previous implementation re-rendered
	 * every node on every keystroke.
	 *
	 * @param {string} term - Text to match, case-insensitive.
	 * @param {Array<string>|null} restore - Branch ids to reopen when the term is empty.
	 * @returns {number} How many nodes matched.
	 */
	filter(term, restore = null) {
		const needle = String(term || '').toLowerCase().trim();
		if (!needle) {
			walk(this.options.nodes, (n) => { n.hidden = false; if (n._el) n._el.hidden = false; });
			this.collapseAll();
			(restore || []).forEach((id) => this.expand(id));
			walk(this.options.nodes, (n) => { if (n._kids) n._kids.hidden = !n.expanded; });
			this.el.classList.remove('dfc-sidebar--filtered');
			return 0;
		}

		let matches = 0;
		// Post-order: a node is kept if it matches or any descendant does.
		const visit = (node) => {
			let keptChild = false;
			for (const kid of node.nodes || []) keptChild = visit(kid) || keptChild;
			const self = String(node.text == null ? '' : node.text).toLowerCase().includes(needle);
			if (self) matches++;
			const keep = self || keptChild;
			node.hidden = !keep;
			if (node._el) node._el.hidden = !keep;
			if (node.nodes && node.nodes.length) {
				node.expanded = keptChild;
				if (node._kids) node._kids.hidden = !keptChild;
				if (node._el) node._el.setAttribute('aria-expanded', keptChild ? 'true' : 'false');
			}
			return keep;
		};
		for (const root of this.options.nodes) visit(root);
		this.el.classList.add('dfc-sidebar--filtered');
		return matches;
	}

	// ── Changing the tree ──────────────────────────────────────────────────

	/**
	 * Adds nodes to the end of a parent, or to the root.
	 * @param {string|Array<Object>} parentId - Parent id, or the nodes themselves for the root.
	 * @param {Array<Object>} [nodes]
	 * @returns {this}
	 */
	add(parentId, nodes) {
		if (Array.isArray(parentId)) { nodes = parentId; parentId = null; }
		return this.insert(parentId, null, nodes);
	}

	/**
	 * Inserts nodes before a sibling, or at the end when `before` is null.
	 * @param {string|null} parentId - Parent id, or null for the root.
	 * @param {string|null} before - Sibling id to insert ahead of.
	 * @param {Array<Object>} nodes
	 * @returns {this}
	 */
	insert(parentId, before, nodes) {
		const list = Array.isArray(nodes) ? nodes : [nodes];
		const parent = parentId == null ? null : this.get(parentId);
		if (parentId != null && !parent) return this;
		const siblings = parent ? (parent.nodes = parent.nodes || []) : this.options.nodes;
		const at = before == null ? siblings.length : siblings.findIndex((n) => String(n.id) === String(before));
		siblings.splice(at < 0 ? siblings.length : at, 0, ...list);
		this.render();
		return this;
	}

	/**
	 * Removes nodes by id.
	 * @param {...string} ids
	 * @returns {this}
	 */
	remove(...ids) {
		const flat = ids.flat ? ids.flat() : ids;
		let changed = false;
		for (const id of flat) {
			const node = this.get(id);
			if (!node) continue;
			const siblings = node.parent ? node.parent.nodes : this.options.nodes;
			const at = siblings.indexOf(node);
			if (at >= 0) { siblings.splice(at, 1); changed = true; }
			if (this._selected !== null && String(this._selected) === String(id)) this._selected = null;
		}
		if (changed) this.render();
		return this;
	}

	/** Replaces the whole tree. @param {Array<Object>} nodes @returns {this} */
	setNodes(nodes) { this.options.nodes = nodes || []; this.render(); return this; }

	// ── Flat (icons only) ──────────────────────────────────────────────────

	/**
	 * Narrows to an icons-only rail, or back.
	 * @param {boolean} [on] - Omit to toggle.
	 * @returns {boolean} Whether it is now flat.
	 */
	goFlat(on) {
		this.options.flat = (on === undefined) ? !this.options.flat : !!on;
		this.el.classList.toggle('dfc-sidebar--flat', this.options.flat);
		this.emit('flat', this._payload({ flat: this.options.flat }));
		return this.options.flat;
	}

	unbind() {
		if (this._hoverTimer) clearTimeout(this._hoverTimer);
		if (this._tip && this._tip.parentNode) { this._tip.parentNode.removeChild(this._tip); this._tip = null; }
		if (this._onClick) this.el.removeEventListener('click', this._onClick);
		if (this._onOver) this.el.removeEventListener('mouseover', this._onOver);
		if (this._onOut) this.el.removeEventListener('mouseout', this._onOut);
		if (this._onKey) this.el.removeEventListener('keydown', this._onKey);
		if (this._onBlur) this.el.removeEventListener('focusout', this._onBlur);
		if (this.events) this.events.clear();
	}
}

export default Sidebar;
